<script>
  // # # # # # # # # # # # # #
  //
  //  List module
  //
  // # # # # # # # # # # # # #

  // *** IMPORT
  import { onMount } from "svelte";
  import { Router, links, link } from "svelte-routing";
  import { fly, slide } from "svelte/transition";

  // *** PROPS
  export let item;

  // *** DOM REFERENCES
  let listItemEl = {};

  // *** VARIABLES
  let active = false;
  let loaded = false;

  // *** CONSTANTS
  const url = item.header.parent + "/" + item.header.slug;

  // *** FUNCTIONS
  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          active = true;
          observer.disconnect();
        }
      });
    },
    { threshold: [0.1, 0.2, 0.3] }
  );

  // *** ON MOUNT
  onMount(async () => {
    observer.observe(listItemEl);
  });
</script>

<style lang="scss">
  @import "../_variables.scss";

  .list-item {
    $block: &;

    font-family: $sans-stack;
    height: 100vh;
    overflow: hidden;
    width: 100%;
    position: relative;
    display: block;
    color: $black;
    background: $white;
    opacity: 0;
    transition: opacity 0.3s ease-out;
    transition-delay: 0.5s;

    &.active {
      opacity: 1;
    }

    &:active {
      .list-item__media {
        opacity: 0.8;
      }
    }

    &.light {
      color: $white;
      background: $black;

      #{ $block }__title {
        color: $white;
      }
    }

    @include screen-size("small") {
      height: auto;
    }

    &__text {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 10;
      overflow: hidden;
      text-align: center;
      user-select: none;
    }

    &__inner-text {
      max-width: 90%;
      width: 60ch;
    }

    &__title {
      border-bottom: none;
      font-size: $font-size-large;
      color: $black;
    }

    &__description {
      font-size: $font-size-large;
      font-weight: 100;
    }

    &__media {
      height: 100%;
      left: 0;
      position: absolute;
      opacity: 1;
      overflow: hidden;
      transition: opacity 0.3s $easing, transform 2s $easing;
      width: 100%;
      z-index: 0;

      @include screen-size("small") {
        height: 500px;
        width: 100%;
        left: 0;
        position: static;
      }

      img,
      video {
        object-fit: cover;
        opacity: 1;
        height: 100%;
        transition: opacity 0.2s $easing;
        width: 100%;
      }
    }

    &:hover {
      .list-item__media {
        transform: scale(1.03);
        transition: opacity 0.3s $easing, transform 0.5s $easing;
      }
    }
  }
</style>

<Router>
  <div class="list-item-container" use:links>
    <a
      href={url}
      class="list-item"
      class:light={item.header.light == 1}
      class:active={active && loaded}
      bind:this={listItemEl}>

      <!-- {# TEXT #} -->
      <div class="list-item__text">

        {#if active && loaded}
          <!-- {# INNER #} -->

          <div class="list-item__inner-text" in:fly={{ y: 50, duration: 800 }}>

            <!-- {# TITLE #} -->
            <div class="list-item__title">{item.header.title}</div>

            <!-- {# DESCRIPTION #} -->
            {#if item.header.description}
              <div class="list-item__description">
                {item.header.description}
              </div>
            {/if}

          </div>
        {/if}

      </div>

      <!-- {# IMAGE / VIDEO #} -->
      <div class="list-item__media">
        {#if item.header.video}
          <video
            src={item.header.video}
            autoplay
            loop
            muted
            on:load={() => (loaded = true)} />
        {:else if item.header.featured_image}
          <img
            srcset="{item.header.featured_image.sizes.small.url}
            {item.header.featured_image.sizes.small.width}w, {item.header.featured_image.sizes.medium.url}
            {item.header.featured_image.sizes.medium.width}w, {item.header.featured_image.sizes.large.url}
            {item.header.featured_image.sizes.large.width}w, {item.header.featured_image.sizes.xlarge.url}
            {item.header.featured_image.sizes.xlarge.width}w"
            sizes="100vw"
            src={item.header.featured_image.sizes.large.url}
            alt={item.header.title}
            on:load={() => (loaded = true)} />
        {/if}
      </div>

    </a>
  </div>
</Router>
